/**
 * Reference Code Mapping for Drainage
 * Maps field names to Evaluate codes (resourceCode in Item Library API)
 * Only "Lab per plot" fields have codes - other fields don't use item library
 */

export interface DrainageReferenceCodeMapping {
  code?: string;
  /** Key in drainageDimensionValues that holds the dynamic item library code (e.g. land drain type) */
  dimensionValueKey?: string;
}

/**
 * Drainage Reference Codes
 * Maps field names from drainage tables to Evaluate codes
 */
export const DRAINAGE_REFERENCES: Record<
  string,
  DrainageReferenceCodeMapping
> = {
  // OSMA FOUL
  osma32mmBend:         { code: "GWALPDD001A" }, // Lab per plot
  osmaPPIC:             { code: "GWALPDD021" },  // PPIC's
  osma50mmBend:         { code: "GWALPDD022" },  // Mini PPIC's
  osma68mmBend:         { code: "GWALPDD023" },  // SVP
  osma82mmBend:         { code: "GWALPDD024" },  // Concrete to rest bends
  osma110mmBend:        { code: "GWALPDD025" },  // 100/100 junctions
  osma32mmPipe:         { code: "GWALPDD026" },  // 100mm pipe
  osma40mmPipe:         { code: "GWALPDD027" },  // 100mm couplers
  osma50mmPipe:         { code: "GWALPDD028" },  // 100/150 junction
  osma68mmPipe:         { code: "GWALPDD029" },  // 150/150 junction
  osma82mmPipe:         { code: "GWALPDD030" },  // 150mm dia pipe
  osma110mmPipe:        { code: "GWALPDD031" },  // 150mm couplers
  gravel:               { code: "GWALPDD032" },  // Gravel
  concealedSetSurround: { code: "GWALPDD033" },  // Concrete bed & surround
  tipOnSite:            { code: "GWALPDD034" },  // Tip onsite
  osmaNmePpic:          { code: "GWALPDD018" },  // NME PPIC
  osma600mmNmePpic:     { code: "GWALPDD052" },  // 600mm NME PPIC

  // OSMA STORM
  osmaStorm32mmBend:                  { code: "GWALPDD001A" }, // Lab per plot
  osmaStorPPIC:                       { code: "GWALPDD021" },  // PPIC's
  osmaStorm50mmBend:                  { code: "GWALPDD035" },  // Rodding Eye
  osmaStorm68mmBend:                  { code: "GWALPDD036" },  // RWP
  osmaStorm100100JunctionTripleSocket: { code: "GWALPDD025" }, // 100/100 Junction (Triple Socket)
  osmaStorm82mmBend:                  { code: "GWALPDD026" },  // 100mm pipe
  osmaStorm110mmBend:                 { code: "GWALPDD027" },  // 100mm couplers
  osmaStorm32mmPipe:                  { code: "GWALPDD028" },  // 100/150 junction
  osmaStorm40mmPipe:                  { code: "GWALPDD029" },  // 150/150 junction
  osmaStorm50mmPipe:                  { code: "GWALPDD030" },  // 150mm dia pipe
  osmaStorm68mmPipe:                  { code: "GWALPDD031" },  // 150mm couplers
  osmaStormGravel:                    { code: "GWALPDD032" },  // Gravel
  osmaStormConcreteBedSurround:       { code: "GWALPDD033" },  // Concrete bed & surround
  osmaStormTipOnsite:                 { code: "GWALPDD034" },  // Tip onsite
  osmaStormNmePpic:                   { code: "GWALPDD018" },  // NME PPIC
  osmaStorm600mmNmePpic:              { code: "GWALPDD052" },  // 600mm NME PPIC
  osmaStormYardGully:                 { code: "GWALPDD051" },  // Yard Gully
  osmaStormDrainageChannel: { dimensionValueKey: "osmaStormDrainageChannel" },
  osmaStormLandDrain:       { dimensionValueKey: "osmaStormLandDrain" },

  // POLYPIPE FOUL
  polypipe32mmBend:              { code: "GWALPDD001A" }, // Lab per plot
  polypipePPIC:                  { code: "GWALPDD037" },  // PPIC's
  polypipe50mmBend:              { code: "GWALPDD038" },  // Mini PPIC's
  polypipe68mmBend:              { code: "GWALPDD039" },  // SVP
  polypipe82mmBend:              { code: "GWALPDD040" },  // Concrete to rest bends
  polypipe110mmBend:             { code: "GWALPDD041" },  // 100/100 junctions
  polypipe32mmPipe:              { code: "GWALPDD042" },  // 100mm pipe
  polypipe40mmPipe:              { code: "GWALPDD043" },  // 100mm couplers
  polypipe50mmPipe:              { code: "GWALPDD044" },  // 100/150 junction
  polypipe68mmPipe:              { code: "GWALPDD045" },  // 150/150 junction
  polypipe82mmPipe:              { code: "GWALPDD046" },  // 150mm dia pipe
  polypipe110mmPipe:             { code: "GWALPDD047" },  // 150mm couplers
  polypipeGravel:                { code: "GWALPDD032" },  // Gravel
  polypipeConcreteBedSurround:   { code: "GWALPDD033" },  // Concrete bed & surround
  polypipeTipOnSite:             { code: "GWALPDD034" },  // Tip onsite
  polypipeNmePpic:               { code: "GWALPDD018" },  // NME PPIC
  polypipe600mmNmePpic:          { code: "GWALPDD052" },  // 600mm NME PPIC

  // POLYPIPE STORM
  polypipeStorm32mmBend:                   { code: "GWALPDD001A" }, // Lab per plot
  polypipeStormPPIC:                       { code: "GWALPDD037" },  // PPIC's
  polypipeStorm50mmBend:                   { code: "GWALPDD048" },  // Rodding Eye
  polypipeStorm68mmBend:                   { code: "GWALPDD049" },  // RWP
  polypipeStorm100100JunctionTripleSocket: { code: "GWALPDD041" },  // 100/100 Junction (Triple Socket)
  polypipeStorm82mmBend:                   { code: "GWALPDD042" },  // 100mm pipe
  polypipeStorm110mmBend:                  { code: "GWALPDD043" },  // 100mm couplers
  polypipeStorm32mmPipe:                   { code: "GWALPDD044" },  // 100/150 junction
  polypipeStorm40mmPipe:                   { code: "GWALPDD045" },  // 150/150 junction
  polypipeStorm50mmPipe:                   { code: "GWALPDD046" },  // 150mm dia pipe
  polypipeStorm68mmPipe:                   { code: "GWALPDD047" },  // 150mm couplers
  polypipeStormGravel:                     { code: "GWALPDD032" },  // Gravel
  polypipeStormConcreteBedSurround:        { code: "GWALPDD033" },  // Concrete bed & surround
  polypipeStormTipOnSite:                  { code: "GWALPDD034" },  // Tip onsite
  polypipeStormNmePpic:                    { code: "GWALPDD018" },  // NME PPIC
  polypipeStorm600mmNmePpic:               { code: "GWALPDD052" },  // 600mm NME PPIC
  polypipeStormYardGully:                  { code: "GWALPDD051" },  // Yard Gully
  polypipeStormDrainageChannel: { dimensionValueKey: "polypipeStormDrainageChannel" },
  polypipeStormLandDrain:       { dimensionValueKey: "polypipeStormLandDrain" },
};

/**
 * Field sets that contribute to each "per-plot" row's combined rate on the
 * Drainage Bill Summary. The per-plot row's displayed rate is
 *   `Σ_field (sumField(field) × itemLibraryRate(field)) ÷ nrPlotsTakeOff`
 * and its amount is `rate × roundedTotalPlots`. The per-section "separate"
 * items (NME PPIC, 600mm NME PPIC, and on Storm sections also Yard Gully /
 * Drainage Channel / Land Drain) are NOT in this list — they stay as their
 * own bill-summary rows.
 */
export const OSMA_FOUL_COMBINED_FIELDS: readonly string[] = [
  'osma32mmBend',
  'osmaPPIC',
  'osma50mmBend',
  'osma68mmBend',
  'osma82mmBend',
  'osma110mmBend',
  'osma32mmPipe',
  'osma40mmPipe',
  'osma50mmPipe',
  'osma68mmPipe',
  'osma82mmPipe',
  'osma110mmPipe',
  'gravel',
  'concealedSetSurround',
  'tipOnSite',
];

export const OSMA_STORM_COMBINED_FIELDS: readonly string[] = [
  'osmaStorm32mmBend',
  'osmaStorPPIC',
  'osmaStorm50mmBend',
  'osmaStorm68mmBend',
  'osmaStorm100100JunctionTripleSocket',
  'osmaStorm82mmBend',
  'osmaStorm110mmBend',
  'osmaStorm32mmPipe',
  'osmaStorm40mmPipe',
  'osmaStorm50mmPipe',
  'osmaStorm68mmPipe',
  'osmaStormGravel',
  'osmaStormConcreteBedSurround',
  'osmaStormTipOnsite',
];

export const POLYPIPE_FOUL_COMBINED_FIELDS: readonly string[] = [
  'polypipe32mmBend',
  'polypipePPIC',
  'polypipe50mmBend',
  'polypipe68mmBend',
  'polypipe82mmBend',
  'polypipe110mmBend',
  'polypipe32mmPipe',
  'polypipe40mmPipe',
  'polypipe50mmPipe',
  'polypipe68mmPipe',
  'polypipe82mmPipe',
  'polypipe110mmPipe',
  'polypipeGravel',
  'polypipeConcreteBedSurround',
  'polypipeTipOnSite',
];

export const POLYPIPE_STORM_COMBINED_FIELDS: readonly string[] = [
  'polypipeStorm32mmBend',
  'polypipeStormPPIC',
  'polypipeStorm50mmBend',
  'polypipeStorm68mmBend',
  'polypipeStorm100100JunctionTripleSocket',
  'polypipeStorm82mmBend',
  'polypipeStorm110mmBend',
  'polypipeStorm32mmPipe',
  'polypipeStorm40mmPipe',
  'polypipeStorm50mmPipe',
  'polypipeStorm68mmPipe',
  'polypipeStormGravel',
  'polypipeStormConcreteBedSurround',
  'polypipeStormTipOnSite',
];

/**
 * Get reference code for a specific field
 */
export const getDrainageReferenceCode = (
  referenceKey: string,
): string | undefined => {
  return DRAINAGE_REFERENCES[referenceKey]?.code;
};

/**
 * Get full reference information for a specific field
 */
export const getDrainageReference = (
  referenceKey: string,
): DrainageReferenceCodeMapping | undefined => {
  return DRAINAGE_REFERENCES[referenceKey];
};
